import { AnimatePresence } from 'framer-motion'
import { useRef } from 'react'

import { getDropInProps } from '@/modules/shared/animations/dropIn.animation'
import { Portal } from '../portal/Portal'
import { AnimatedStModalContainer, StModalWrapper } from './Modal.styled'
import { ModalBackdrop } from './ModalBackdrop'

import type { MouseEvent } from 'react'
import type { ModalProps } from './Modal.props'

const Modal = ({
  disabled,
  onClose,
  isVisible,
  children,
  fullScreen,
  noPadding,
  backdropColor,
  maxHeight,
}: ModalProps) => {
  const mouseDownTargetRef = useRef<EventTarget | null>(null)

  function handleClose() {
    if (!disabled && onClose) {
      onClose()
    }
  }

  function handleModalClick(event: MouseEvent<HTMLDivElement>) {
    event.stopPropagation()
  }

  function handleBackdropMouseDown(event: MouseEvent<HTMLDivElement>) {
    mouseDownTargetRef.current = event.target
  }

  function handleBackdropClick(event: MouseEvent<HTMLDivElement>) {
    // Only close when both mousedown and mouseup occurred on the backdrop.
    // This prevents accidental closes when the user clicks inside the modal
    // content and drags the mouse onto the backdrop before releasing.
    if (mouseDownTargetRef.current === event.target) {
      handleClose()
    }
    mouseDownTargetRef.current = null
  }

  return (
    <Portal wrapperId='modal-container'>
      <AnimatePresence initial={false} mode='wait'>
        {isVisible ? (
          <StModalWrapper>
            <ModalBackdrop onClick={handleBackdropClick} onMouseDown={handleBackdropMouseDown}>
              <AnimatedStModalContainer
                fullScreen={fullScreen}
                noPadding={noPadding}
                backdropColor={backdropColor}
                maxHeight={maxHeight}
                layout='position'
                onClick={handleModalClick}
                {...getDropInProps({
                  hidden: { y: -30 },
                  exit: { opacity: 0, y: 30, scale: 0.8 },
                })}
              >
                {children}
              </AnimatedStModalContainer>
            </ModalBackdrop>
          </StModalWrapper>
        ) : null}
      </AnimatePresence>
    </Portal>
  )
}

export { Modal }
